/**
 * Related-work reads — the bounded, per-trace queries the panel scores over.
 *
 * There is deliberately NO corpus here. This used to project every
 * (session, resource_path) pair in the tenant's lookback window into a
 * module-level cache (~142K rows on the reference tenant) because the backend
 * could not answer "which sessions touched this path". It can now:
 * PROMPT_RESOURCE_PATHS is an Array(String) dimension, filterable element-wise
 * via LIKE (arrayExists + an ngram-indexed prefilter on the joined-text
 * column), so the neighbourhood of ONE session is a bounded server-side read.
 *
 * Four reads per trace, none of them corpus-scale:
 *   1. fetchSessionPaths   — the query session's own paths (the needles).
 *   2. fetchSessionCount   — N, one scalar. The only cached value.
 *   3. fetchAnchorNeighbourhood — every session touching any needle.
 *   4. fetchSessionMeta    — start/end/endpoint for the candidates, by id.
 *
 * Fanout stays EXACT: read (3) returns every session touching a needle, so
 * counting distinct sessions per needle in its rows is the true tenant-wide
 * fanout, not a sample.
 */

import {
    runQuery,
    rowsToObjects,
    buildPromptRootAndFilter,
} from "../analyticsClient.js";
import { promptCount } from "../promptMetrics.js";
import { parseServerTimestamp } from "../traceUtils.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../authTokenStore.js";

export const LOOKBACK_DAYS = 90;

// Needles become OR'd LIKE predicates, two per needle (see `suffixPatterns`),
// and cost scales with that count: measured 0.1s at 7 needles, ~1s at 68,
// ~3.3s at 250. 250 covers 98.5% of sessions on the reference tenant (post
// shape-gate p90 = 68, p99 = 335); the rest are truncated and say so.
const MAX_NEEDLES = 250;
const NEIGHBOURHOOD_ROW_LIMIT = 60000;
// The neighbourhood is ~95% of the panel's cost and repeats verbatim for every
// trace in a session, so without this the bill scales with click-through rather
// than with sessions viewed. runQuery's shared result cache defaults to 30s,
// which is too short to cover a read of a trace list.
//
// This is NOT the corpus coming back. That was one tenant-wide projection
// (~142K rows) that every session scored against, so a stale or truncated copy
// silently degraded every suggestion in the tab. These entries are keyed on the
// exact spec — hence on one session's needle set — hold 2.1K rows at median and
// 18.6K at p99, ride the existing 500-entry LRU alongside every other dashboard
// query, and can only ever affect the one session that produced them.
const NEIGHBOURHOOD_STALE_MS = 5 * 60 * 1000;
// Server-side IN lists are capped at 1024 (shared-analytics MAX_IN_LIST_LEN).
const META_ID_LIMIT = 900;
const SESSION_PATHS_ROW_LIMIT = 5000;
// How far past the trace the session-paths window reaches, so a session whose
// later prompts follow the clicked trace is still covered.
const SESSION_WINDOW_LEAD_MS = 86400 * 1000;
const COUNT_CACHE_TTL_MS = 30 * 60 * 1000;

const F_SESSION = "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID";
const F_PATHS = "DIMENSION_FIELD_PROMPT_RESOURCE_PATHS";
const F_TS = "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP";
const F_ENDPOINT = "DIMENSION_FIELD_PROMPT_ENDPOINT_ID";

const ref = (field) => ({ stepAlias: "p", field });

export function lookbackTimeRange(days = LOOKBACK_DAYS) {
    return { relative: { durationSeconds: String(days * 86400) } };
}

// Escape the LIKE metacharacters so a path is matched literally. `_` matters:
// it is a single-character wildcard and is extremely common in real paths.
// This is LIKE-level escaping only. The backend separately encodes the logical
// pattern for ClickHouse's HTTP parameter grammar.
function likeLiteral(s) {
    return s.replace(/[\\%_]/g, (c) => `\\${c}`);
}

/**
 * The LIKE patterns that match a path ending in `suffix`.
 *
 * Two, because the anchor key is separator-normalized but the stored path is
 * not: a Windows endpoint writes `…\src\main.rs` for what a macOS one writes as
 * `…/src/main.rs`, and the suffix collapse exists precisely to relate those.
 *
 * Case-SENSITIVE by design. The ngram prefilter backing resource_paths_text is
 * case-sensitive and the planner drops the prefilter for ILIKE, which turns a
 * granule-pruned read into a full scan per term. Measured on the reference
 * tenant, case-insensitive matching changed nothing — full-path matching
 * lowercased and case-sensitive produced identical suggestions — so this takes
 * the index and accepts that a path differing only in case is missed. Patterns
 * are therefore built from the ORIGINAL-case suffix, never the lowercased key.
 */
export function suffixPatterns(suffix) {
    return [
        `%/${likeLiteral(suffix)}`,
        `%${likeLiteral(`\\${suffix.replace(/\//g, "\\")}`)}`,
    ];
}

/**
 * The window a session's paths are read over.
 *
 * NOW-anchored whenever the trace falls inside the lookback, which is the case
 * that matters: the window must not depend on WHICH trace of a session was
 * opened. A trace-anchored window ends a fixed lead past that trace, so on a
 * session running longer than the lead an early trace sees fewer of the
 * session's paths than a late one — different needles, so different
 * suggestions for the same session, and a different query-cache key for every
 * trace. On the reference tenant 11% of sessions run past a one-day lead (p90
 * span 26.5h, max 25 days), so that is a routine case, not a corner.
 *
 * The trace-anchored fallback exists only for a trace OLDER than the lookback,
 * which a now-anchored window cannot see at all. That was this window's sole
 * original purpose; putting it on the hot path was the mistake.
 *
 * The relative form also serializes identically on every call, so the spec —
 * and therefore the cache key derived from it — is stable over time.
 */
function sessionPathsTimeRange(traceTsMs) {
    const lookbackMs = LOOKBACK_DAYS * 86400 * 1000;
    if (Date.now() - traceTsMs < lookbackMs) return lookbackTimeRange();
    // The lead and the lookback SHARE the window budget — they do not add. An
    // absolute range wider than LOOKBACK_DAYS is rejected outright
    // (shared-analytics MAX_ABSOLUTE_RANGE_SECS).
    const toMs = traceTsMs + SESSION_WINDOW_LEAD_MS;
    return {
        absolute: {
            start: new Date(toMs - lookbackMs).toISOString(),
            end: new Date(toMs).toISOString(),
        },
    };
}

/**
 * Resource paths for one session, most-touched first.
 *
 * @returns {Promise<Array<{ path: string, prompts: number }>>}
 */
export async function fetchSessionPaths(sessionId, traceTsMs) {
    const { root } = buildPromptRootAndFilter({});
    const spec = {
        root,
        timeRange: sessionPathsTimeRange(traceTsMs),
        // Single-element IN — the proven shape sessionLabels and the session
        // dim's own breakdowns use for a one-session predicate.
        filter: { pred: { field: ref(F_SESSION), op: "OPERATOR_IN", value: [String(sessionId)] } },
        output: {
            dimensions: [{ field: ref(F_PATHS), outputAlias: "path" }],
            metrics: [promptCount("n")],
        },
        limit: SESSION_PATHS_ROW_LIMIT,
    };
    const rows = rowsToObjects(await runQuery(spec));
    return rows
        .map((r) => ({ path: String(r.path ?? ""), prompts: Number(r.n) || 0 }))
        .filter((r) => r.path);
}

// N — distinct sessions in the lookback window. Feeds both the anchor weight
// (ln(N / fanout)) and the fanout band. One row, so a cached value cannot
// produce wrong evidence: it only shifts the weight scale and the cut, and it
// is keyed by identity so an org change re-reads.
let _countCache = null; // { key, promise, time }

function identityKey() {
    return getAuthIdentityEpoch();
}
onAuthIdentityChange(() => { _countCache = null; });

async function querySessionCount() {
    const { root } = buildPromptRootAndFilter({});
    const rows = rowsToObjects(await runQuery({
        root,
        timeRange: lookbackTimeRange(),
        // Sessions that touched at least one path — NOT every session. The
        // weight denominator and the band are both calibrated against that
        // population (the pair projection this replaced could only ever see
        // path-bearing sessions), and on the reference tenant the two differ
        // by 2.8x: 1950 with paths against 5444 overall. Counting all sessions
        // inflates every weight by ln(2.8), which is not uniform across
        // candidates once scores are summed, and pushes the band from 46 to 77.
        // `LIKE '%'` lowers to arrayExists(x -> x LIKE '%', …) — true for a
        // non-empty array — because the array operator allowlist has no
        // IS_NOT_NULL.
        filter: { pred: { field: ref(F_PATHS), op: "OPERATOR_LIKE", value: "%" } },
        output: {
            metrics: [{
                outputAlias: "n",
                computed: { field: ref(F_SESSION), op: "AGGREGATE_OP_COUNT_DISTINCT" },
            }],
        },
    }));
    return Number(rows[0]?.n) || 0;
}

export function fetchSessionCount() {
    const key = identityKey();
    const now = Date.now();
    if (_countCache && _countCache.key === key && now - _countCache.time < COUNT_CACHE_TTL_MS) {
        return _countCache.promise;
    }
    const promise = querySessionCount().then((count) => {
        if (identityKey() !== key) {
            const error = new Error("Authentication identity changed while loading related-work counts");
            error.name = "AuthContextChangedError";
            throw error;
        }
        return count;
    });
    _countCache = { key, promise, time: now };
    promise.catch(() => {
        if (_countCache?.promise === promise) _countCache = null;
    });
    return promise;
}

export function resetRelatedWorkCachesForTests() {
    _countCache = null;
}

/**
 * Every session touching any of `suffixes`, with the paths of the matching
 * prompts.
 *
 * Rows are (session, path) at prompt-array grain: the group-by explodes each
 * matching prompt's WHOLE path array, not just the matched elements, so the
 * caller filters to the anchor keys it asked for. Measured 2.1K rows at 10
 * needles, 18.6K at 465 — bounded enough to do that in the browser.
 *
 * @param {Array<string>} suffixes original-case last-3-segment suffixes
 * @returns {Promise<{ pairs: Array<{ sid: string, path: string }>, truncated: boolean }>}
 */
export async function fetchAnchorNeighbourhood(suffixes) {
    if (!suffixes.length) return { pairs: [], truncated: false };
    const { root } = buildPromptRootAndFilter({});
    const children = suffixes.flatMap((s) =>
        suffixPatterns(s).map((value) => ({
            pred: { field: ref(F_PATHS), op: "OPERATOR_LIKE", value },
        })),
    );
    const spec = {
        root,
        timeRange: lookbackTimeRange(),
        filter: children.length === 1 ? children[0] : { orExpr: { children } },
        output: {
            dimensions: [
                { field: ref(F_SESSION), outputAlias: "sid" },
                { field: ref(F_PATHS), outputAlias: "path" },
            ],
            metrics: [promptCount("n")],
        },
        limit: NEIGHBOURHOOD_ROW_LIMIT,
    };
    const rows = rowsToObjects(await runQuery(spec, { staleMs: NEIGHBOURHOOD_STALE_MS }));
    const pairs = [];
    for (const r of rows) {
        const sid = String(r.sid ?? "");
        const path = String(r.path ?? "");
        if (sid && path) pairs.push({ sid, path });
    }
    return { pairs, truncated: rows.length >= NEIGHBOURHOOD_ROW_LIMIT };
}

/**
 * Session start / end / endpoint, by id.
 *
 * A separate read rather than metrics on the neighbourhood query: that query is
 * filtered to prompts matching a needle, so its min/max timestamps would be the
 * first and last MATCHING prompt, not the session's real span — which would
 * wrongly exclude a session that started before the query session but whose
 * first shared file came later.
 *
 * @param {Array<string>} sessionIds capped at META_ID_LIMIT by the caller's ranking
 * @returns {Promise<Map<string, { tsMinMs: number, tsMaxMs: number, endpointId: string|null }>>}
 */
export async function fetchSessionMeta(sessionIds) {
    const ids = sessionIds.slice(0, META_ID_LIMIT).map(String);
    const out = new Map();
    if (!ids.length) return out;
    const { root } = buildPromptRootAndFilter({});
    const rows = rowsToObjects(await runQuery({
        root,
        timeRange: lookbackTimeRange(),
        filter: { pred: { field: ref(F_SESSION), op: "OPERATOR_IN", value: ids } },
        output: {
            dimensions: [
                { field: ref(F_SESSION), outputAlias: "sid" },
                { field: ref(F_ENDPOINT), outputAlias: "endpoint_id" },
            ],
            metrics: [
                { outputAlias: "ts_min", computed: { field: ref(F_TS), op: "AGGREGATE_OP_MIN" } },
                { outputAlias: "ts_max", computed: { field: ref(F_TS), op: "AGGREGATE_OP_MAX" } },
            ],
        },
        limit: META_ID_LIMIT * 4,
    }));
    // A session can group into several rows if it spans endpoints (it
    // shouldn't, but the data is allowed to disagree) — merge to one record
    // with the widest time span.
    for (const r of rows) {
        const sid = String(r.sid ?? "");
        if (!sid) continue;
        const tsMinMs = parseServerTimestamp(r.ts_min);
        const tsMaxMs = parseServerTimestamp(r.ts_max);
        if (!Number.isFinite(tsMinMs) || !Number.isFinite(tsMaxMs)) continue;
        const prev = out.get(sid);
        if (!prev) {
            out.set(sid, { tsMinMs, tsMaxMs, endpointId: r.endpoint_id ? String(r.endpoint_id) : null });
        } else {
            prev.tsMinMs = Math.min(prev.tsMinMs, tsMinMs);
            prev.tsMaxMs = Math.max(prev.tsMaxMs, tsMaxMs);
        }
    }
    return out;
}

export const RELATED_WORK_LIMITS = { MAX_NEEDLES, META_ID_LIMIT, NEIGHBOURHOOD_ROW_LIMIT };
